import React from 'react';
import { ChannelStatus } from 'types/channel';

export type SubscribeButtonState =
  | 'not_subscribed'
  | 'subscribed'
  | 'mandatory'
  | 'manage'
  | 'pending_review'
  | 'rejected';

export interface SubscribeButtonAtomProps {
  state: SubscribeButtonState;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
}

export function deriveSubscribeButtonState(opts: {
  isOwner: boolean;
  status: ChannelStatus;
  isSubscribed: boolean;
  isMandatory: boolean;
}): SubscribeButtonState {
  const { isOwner, status, isSubscribed, isMandatory } = opts;

  if (isOwner) {
    if (status === 'approved') return 'manage';
    if (status === 'pending_review') return 'pending_review';
    if (status === 'rejected') return 'rejected';
    if (status === 'suspended') return 'rejected';
  }

  if (isMandatory) return 'mandatory';
  if (isSubscribed) return 'subscribed';
  return 'not_subscribed';
}

const BUTTON_CONFIG: Record<
  SubscribeButtonState,
  { label: string; icon: string | null; disabled: boolean; title: string | null; variant: string }
> = {
  not_subscribed: {
    label: 'Subscribe',
    icon: 'pi pi-plus',
    disabled: false,
    title: null,
    variant: 'outline',
  },
  subscribed: {
    label: 'Subscribed',
    icon: '\u2713',
    disabled: false,
    title: null,
    variant: 'filled',
  },
  mandatory: {
    label: 'Required',
    icon: '\uD83D\uDD12',
    disabled: true,
    title: 'Required channel',
    variant: 'filled',
  },
  manage: {
    label: 'Manage',
    icon: '\u2192',
    disabled: false,
    title: null,
    variant: 'outline',
  },
  pending_review: {
    label: 'Pending Review',
    icon: '\u23F3',
    disabled: true,
    title: 'Awaiting admin approval',
    variant: 'muted',
  },
  rejected: {
    label: 'Rejected',
    icon: '\u274C',
    disabled: false,
    title: null,
    variant: 'danger',
  },
};

export const SubscribeButtonAtom: React.FC<SubscribeButtonAtomProps> = ({
  state,
  loading = false,
  onClick,
  className = '',
}) => {
  const config = BUTTON_CONFIG[state];
  const isDisabled = config.disabled || loading;

  const classes = [
    'marketplace-subscribe-btn',
    `marketplace-subscribe-btn--${config.variant}`,
    loading ? 'marketplace-subscribe-btn--loading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      disabled={isDisabled}
      title={config.title || undefined}
      onClick={isDisabled ? undefined : onClick}
    >
      {loading ? (
        <span className="marketplace-subscribe-btn__spinner" />
      ) : (
        <>
          {config.icon &&
            (config.icon.startsWith('pi ') ? (
              <i
                className={`${config.icon} marketplace-subscribe-btn__icon`}
                aria-hidden="true"
              />
            ) : (
              <span
                className="marketplace-subscribe-btn__icon"
                aria-hidden="true"
              >
                {config.icon}
              </span>
            ))}
          <span className="marketplace-subscribe-btn__label">{config.label}</span>
        </>
      )}
    </button>
  );
};

/** @deprecated Use SubscribeButtonAtom instead */
export const SubscribeButton = SubscribeButtonAtom;
/** @deprecated Use deriveSubscribeButtonState instead */
export const deriveState = deriveSubscribeButtonState;
